import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken, clearToken, resolveMediaUrl } from "../lib/api";
import Logo from "../components/Logo";
import Hero from "../components/Hero";
import Regional from "../components/Regional";
import WhatWeOffer from "../components/WhatWeOffer";
import Services from "../components/Services";
import Capabilities from "../components/Capabilities";
import About from "../components/About";
import Contact from "../components/Contact";
import Portfolio from "../components/Portfolio";
import Footer from "../components/Footer";
import { toast } from "sonner";
import {
  LogOut,
  Save,
  Eye,
  Image as ImageIcon,
  Plus,
  Trash2,
  Mail,
  Sparkles,
  X,
  GripVertical,
  Edit3,
  FileText,
  Star,
} from "lucide-react";

const TABS = [
  { id: "preview", label: "Live Editor" },
  { id: "projects", label: "Portfolio" },
  { id: "blogs", label: "Blogs" },
  { id: "contact", label: "Submissions" },
  { id: "media", label: "Media Studio" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("preview");
  const [content, setContent] = useState({});
  const [projects, setProjects] = useState([]);
  const [contactSubs, setContactSubs] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.getContent(), api.getProjects()]);
      setContent(c || {});
      setProjects(p || []);
    } catch (err) {
      console.error("Failed to load content/projects:", err);
      toast.error("Failed to load dashboard data.");
    }
    try {
      const subs = await api.listContact();
      setContactSubs(subs || []);
    } catch (err) {
      console.error("Failed to load contact submissions:", err);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    api.me().catch((err) => {
      console.error("Admin auth check failed:", err);
      clearToken();
      nav("/admin/login");
    });
    refresh();
  }, [nav, refresh]);

  const updateSection = (key, data) => {
    setContent((prev) => ({ ...prev, [key]: data }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const keys = Object.keys(dirty).filter((k) => dirty[k]);
      await Promise.all(keys.map((k) => api.updateContent(k, content[k])));
      toast.success("Changes published live.");
      setDirty({});
    } catch (e) {
      toast.error("Save failed. Check connection.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  const hasDirty = Object.values(dirty).some(Boolean);

  return (
    <div className="min-h-screen bg-onyx-900 text-ivory editor-mode" data-testid="admin-dashboard">
      <header className="sticky top-0 z-50 border-b border-gold/15 bg-onyx-900/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Logo size={64} withText />
            <div>
              <h1 className="font-cinzel text-xs tracking-[0.3em] uppercase text-gold">
                Director's Suite
              </h1>
              <p className="text-[10px] text-ivory/50 mt-0.5">Live inline editor</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-2 text-ivory/70 hover:text-gold font-cinzel text-[10px] tracking-[0.3em] uppercase border border-gold/30 hover:border-gold px-4 py-2"
              data-testid="admin-view-site"
            >
              <Eye size={14} /> View Live Site
            </a>
            <button
              onClick={saveAll}
              disabled={!hasDirty || saving}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-bright disabled:opacity-30 disabled:cursor-not-allowed text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-5 py-2.5 transition-colors"
              data-testid="admin-save-btn"
            >
              <Save size={14} />
              {(() => {
                if (saving) return "Saving…";
                if (hasDirty) return "Publish Changes";
                return "Saved";
              })()}
            </button>
            <button
              onClick={logout}
              className="text-ivory/60 hover:text-gold p-2"
              data-testid="admin-logout-btn"
              aria-label="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-[1600px] px-6 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative font-cinzel text-[10px] tracking-[0.3em] uppercase px-5 py-3 transition-colors whitespace-nowrap ${
                tab === t.id ? "text-gold" : "text-ivory/55 hover:text-ivory"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
              {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-gold" />}
            </button>
          ))}
        </div>
      </header>

      <main>
        {tab === "preview" && (
          <LiveEditor
            content={content}
            updateSection={updateSection}
            projects={projects}
          />
        )}
        {tab === "projects" && (
          <ProjectsManager projects={projects} refresh={refresh} />
        )}
        {tab === "blogs" && <BlogsManager />}
        {tab === "contact" && (
          <ContactInbox subs={contactSubs} refresh={refresh} />
        )}
        {tab === "media" && <MediaStudio />}
      </main>
    </div>
  );
}

// ============== Live Editor ==============
const LiveEditor = ({ content, updateSection, projects }) => {
  const [mediaTarget, setMediaTarget] = useState(null);

  const setMediaUrl = (sectionKey, fieldKey, url) => {
    const section = content[sectionKey] || {};
    updateSection(sectionKey, { ...section, [fieldKey]: url });
    setMediaTarget(null);
    toast.success("Media updated. Don't forget to publish.");
  };

  return (
    <div data-testid="live-editor">
      <div className="bg-gold/10 border-b border-gold/20 px-6 py-3 text-center text-xs text-ivory/70">
        <Edit3 size={12} className="inline mr-2 text-gold" />
        Click any text to edit. Click "Replace Background / Image" to swap media.
      </div>
      <Hero
        data={content.hero || {}}
        editable
        onChange={(d) => updateSection("hero", d)}
        onMediaClick={() => setMediaTarget({ section: "hero", field: "background_url" })}
      />
      <Regional
        data={content.regional || {}}
        editable
        onChange={(d) => updateSection("regional", d)}
        onMediaClick={() => setMediaTarget({ section: "regional", field: "image_url" })}
      />
      <WhatWeOffer />
      <Services
        data={content.services || {}}
        editable
        onChange={(d) => updateSection("services", d)}
      />
      <Capabilities />
      <Portfolio projects={projects} showHeader />
      <About
        data={content.about || {}}
        editable
        onChange={(d) => updateSection("about", d)}
        onMediaClick={() => setMediaTarget({ section: "about", field: "image_url" })}
      />
      <Contact
        data={content.contact || {}}
        editable
        onChange={(d) => updateSection("contact", d)}
      />
      <Footer contact={content.contact || {}} settings={content.settings || {}} />

      {mediaTarget && (
        <MediaPicker
          onClose={() => setMediaTarget(null)}
          onSelect={(url) => setMediaUrl(mediaTarget.section, mediaTarget.field, url)}
        />
      )}
    </div>
  );
};

// ============== Media Picker (drag-drop / upload / AI) ==============
const MediaPicker = ({ onClose, onSelect }) => {
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(
    "Cinematic Bollywood vanity van on a Mumbai film set, golden hour, paparazzi shot, premium silver-black livery"
  );
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadFile(file);
      onSelect(res.url);
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleAI = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      toast.message("Generating cinematic frame… (~15s)");
      const res = await api.generateImage(prompt);
      onSelect(res.url);
    } catch (e) {
      toast.error("AI generation failed. Try upload instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-onyx-900/95 backdrop-blur-md p-4" onClick={onClose} data-testid="media-picker">
      <div className="relative w-full max-w-2xl gold-border bg-onyx-800" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gold/15 px-6 py-4">
          <div className="flex items-center gap-3">
            <ImageIcon size={18} className="text-gold" />
            <h3 className="font-cinzel text-xs tracking-[0.3em] uppercase text-ivory">Replace Media</h3>
          </div>
          <button onClick={onClose} className="text-ivory/60 hover:text-gold" data-testid="media-picker-close"><X size={18} /></button>
        </div>

        <div className="flex border-b border-gold/15">
          {[
            { id: "upload", label: "Upload" },
            { id: "ai", label: "AI Generate" },
            { id: "url", label: "From URL" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-3 font-cinzel text-[10px] tracking-[0.3em] uppercase transition-colors ${
                tab === t.id ? "text-gold border-b border-gold" : "text-ivory/55 hover:text-ivory"
              }`}
              data-testid={`media-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-8">
          {tab === "upload" && (
            <div
              className={`border-2 border-dashed ${drag ? "border-gold bg-gold/10" : "border-gold/30"} p-12 text-center cursor-pointer transition-colors`}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                handleFile(e.dataTransfer.files[0]);
              }}
              onClick={() => fileRef.current?.click()}
              data-testid="media-dropzone"
            >
              <ImageIcon size={36} className="text-gold mx-auto mb-4" strokeWidth={1.2} />
              <p className="text-ivory/85">Drag & drop an image or video</p>
              <p className="text-xs text-ivory/45 mt-2">Or click to browse — JPG, PNG, MP4</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="media-file-input"
              />
              {busy && <p className="text-gold mt-4 text-xs font-cinzel tracking-[0.3em] uppercase">Uploading…</p>}
            </div>
          )}

          {tab === "ai" && (
            <div>
              <label className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold/70">
                Describe the cinematic frame
              </label>
              <textarea
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="input-cinema mt-3 w-full resize-none"
                data-testid="ai-prompt-input"
              />
              <button
                onClick={handleAI}
                disabled={busy}
                className="mt-6 inline-flex items-center gap-2 bg-gold hover:bg-gold-bright disabled:opacity-50 text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-6 py-3"
                data-testid="ai-generate-btn"
              >
                <Sparkles size={14} />
                {busy ? "Generating…" : "Generate with Nano Banana"}
              </button>
              <p className="mt-4 text-[11px] text-ivory/45">
                Powered by Gemini 3 Flash Image. The gold "D" watermark is automatically overlaid by the website.
              </p>
            </div>
          )}

          {tab === "url" && (
            <div>
              <label className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold/70">
                Direct image / video URL
              </label>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://"
                className="input-cinema mt-3 w-full"
                data-testid="media-url-input"
              />
              <button
                onClick={() => urlInput && onSelect(urlInput)}
                disabled={!urlInput}
                className="mt-6 bg-gold hover:bg-gold-bright disabled:opacity-50 text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-6 py-3"
                data-testid="media-url-apply"
              >
                Apply URL
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== Projects Manager ==============
const ProjectsManager = ({ projects, refresh }) => {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", youtube_url: "", category: "Music Video", order: 0 });
  const [editing, setEditing] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.youtube_url) return;
    try {
      await api.createProject({ ...form, order: projects.length });
      toast.success("Project added.");
      setForm({ title: "", youtube_url: "", category: "Music Video", order: 0 });
      setCreating(false);
      refresh();
    } catch {
      toast.error("Failed to create.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project permanently?")) return;
    await api.deleteProject(id);
    toast.success("Project deleted.");
    refresh();
  };

  const saveEdit = async () => {
    try {
      await api.updateProject(editing.id, {
        title: editing.title,
        youtube_url: editing.youtube_url,
        category: editing.category,
        order: editing.order,
      });
      toast.success("Project updated.");
      setEditing(null);
      refresh();
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-12" data-testid="projects-manager">
      <div className="gold-border bg-onyx-800 p-6 mb-8">
        <h3 className="font-cinzel text-xs tracking-[0.3em] uppercase text-gold mb-2 flex items-center gap-2">
          <Star size={14} /> Quick Add — Latest Project
        </h3>
        <p className="text-ivory/55 text-sm mb-5">
          Paste any YouTube URL — the thumbnail and watermark are auto-generated. New
          projects appear at the top of the home reel.
        </p>
        <QuickAddProject onCreated={refresh} totalCount={projects.length} />
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-display text-3xl text-ivory">Portfolio Library</h2>
          <p className="text-ivory/55 text-sm mt-1">{projects.length} projects published</p>
        </div>
        <button
          onClick={() => setCreating(!creating)}
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold-bright text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-5 py-3"
          data-testid="add-project-btn"
        >
          <Plus size={14} /> Add Project
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="gold-border bg-onyx-800 p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="new-project-form">
          <input className="input-cinema" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-project-title" />
          <input className="input-cinema" placeholder="YouTube URL" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} data-testid="new-project-url" />
          <select className="input-cinema bg-onyx-800" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option>Music Video</option>
            <option>Commercial</option>
            <option>Documentary</option>
            <option>Short Film</option>
            <option>Feature Film</option>
          </select>
          <button type="submit" className="bg-gold hover:bg-gold-bright text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase">
            Create
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.map((p) => (
          <div key={p.id} className="gold-border bg-onyx-800 overflow-hidden group" data-testid={`project-row-${p.id}`}>
            <div className="aspect-video bg-onyx-700 relative overflow-hidden">
              <img src={p.custom_thumbnail || p.thumbnail_url} alt={p.title} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
            </div>
            <div className="p-4">
              {editing?.id === p.id ? (
                <div className="space-y-3">
                  <input className="input-cinema w-full" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                  <input className="input-cinema w-full" value={editing.youtube_url} onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })} />
                  <input className="input-cinema w-full" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="flex-1 bg-gold text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase py-2">Save</button>
                    <button onClick={() => setEditing(null)} className="flex-1 border border-gold/30 text-ivory font-cinzel text-[10px] tracking-[0.3em] uppercase py-2">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold">{p.category}</span>
                  <h3 className="font-display text-xl text-ivory mt-1">{p.title}</h3>
                  <a href={p.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-ivory/45 hover:text-gold break-all">{p.youtube_url}</a>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditing(p)} className="flex-1 border border-gold/30 hover:border-gold text-ivory font-cinzel text-[10px] tracking-[0.3em] uppercase py-2" data-testid={`edit-project-${p.id}`}>Edit</button>
                    <button onClick={() => remove(p.id)} className="border border-red-500/40 hover:border-red-500 text-red-400 px-4 py-2" data-testid={`delete-project-${p.id}`}><Trash2 size={14} /></button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== Contact Inbox ==============
const ContactInbox = ({ subs, refresh }) => {
  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.deleteContact(id);
    refresh();
  };
  return (
    <div className="mx-auto max-w-[1400px] px-6 py-12" data-testid="contact-inbox">
      <h2 className="font-display text-3xl text-ivory">Contact Submissions</h2>
      <p className="text-ivory/55 text-sm mt-1 mb-8">{subs.length} enquiries received</p>
      <div className="space-y-4">
        {subs.length === 0 && (
          <div className="gold-border bg-onyx-800 p-12 text-center text-ivory/50">
            <Mail size={32} className="text-gold/50 mx-auto mb-4" />
            No submissions yet. Share the site to start collecting enquiries.
          </div>
        )}
        {subs.map((s) => (
          <div key={s.id} className="gold-border bg-onyx-800 p-6" data-testid={`sub-${s.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-display text-2xl text-ivory">{s.name}</h3>
                  {s.project_type && <span className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold border border-gold/30 px-2 py-0.5">{s.project_type}</span>}
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-ivory/55 mb-3">
                  <a href={`mailto:${s.email}`} className="hover:text-gold">{s.email}</a>
                  {s.phone && <a href={`tel:${s.phone}`} className="hover:text-gold">{s.phone}</a>}
                  <span>{new Date(s.created_at).toLocaleString()}</span>
                </div>
                <p className="text-ivory/80 leading-relaxed">{s.message}</p>
              </div>
              <button onClick={() => remove(s.id)} className="text-ivory/40 hover:text-red-400 p-2" data-testid={`delete-sub-${s.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== Media Studio (gallery of uploads) ==============
const MediaStudio = () => {
  return (
    <div className="mx-auto max-w-[1400px] px-6 py-12" data-testid="media-studio">
      <h2 className="font-display text-3xl text-ivory">Media Studio</h2>
      <p className="text-ivory/55 text-sm mt-1 mb-8">
        Generate cinematic visuals or upload your own. All media auto-receives the gold "D" watermark when displayed.
      </p>
      <MediaPickerInline />
    </div>
  );
};

const MediaPickerInline = () => {
  const [generated, setGenerated] = useState([]);
  const [prompt, setPrompt] = useState(
    "Cinematic Bollywood vanity van on Mumbai Film City set, paparazzi shot, golden hour, premium silver-black livery, marigold garlands"
  );
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const fileRef = useRef(null);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.generateImage(prompt);
      setGenerated((g) => [{ ...res, prompt }, ...g]);
      toast.success("Frame generated.");
    } catch {
      toast.error("Generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadFile(file);
      setUploaded((u) => [{ ...res, name: file.name }, ...u]);
      toast.success("Uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="gold-border bg-onyx-800 p-6">
        <h3 className="font-cinzel text-xs tracking-[0.3em] uppercase text-gold mb-4 flex items-center gap-2">
          <Sparkles size={14} /> AI Generator
        </h3>
        <textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="input-cinema w-full resize-none" data-testid="studio-ai-prompt" />
        <button onClick={generate} disabled={busy} className="mt-4 bg-gold hover:bg-gold-bright disabled:opacity-50 text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-5 py-3" data-testid="studio-ai-generate">
          {busy ? "Generating…" : "Generate"}
        </button>
        <div className="mt-6 grid grid-cols-2 gap-3">
          {generated.map((g, i) => (
            <a key={g.url || `gen-${i}`} href={resolveMediaUrl(g.url)} target="_blank" rel="noreferrer" className="block aspect-video overflow-hidden gold-border">
              <img src={resolveMediaUrl(g.url)} alt={g.prompt} className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      </div>

      <div className="gold-border bg-onyx-800 p-6">
        <h3 className="font-cinzel text-xs tracking-[0.3em] uppercase text-gold mb-4 flex items-center gap-2">
          <ImageIcon size={14} /> Upload Library
        </h3>
        <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gold/30 p-8 text-center hover:border-gold transition-colors" data-testid="studio-upload-trigger">
          <p className="text-ivory/85">Click to upload image or video</p>
          <p className="text-xs text-ivory/45 mt-1">JPG, PNG, MP4</p>
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <div className="mt-6 grid grid-cols-2 gap-3">
          {uploaded.map((u, i) => (
            <a key={u.url || `up-${i}`} href={resolveMediaUrl(u.url)} target="_blank" rel="noreferrer" className="block aspect-video overflow-hidden gold-border">
              <img src={resolveMediaUrl(u.url)} alt={u.name} className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};


// ============== Quick Add Project ==============
const QuickAddProject = ({ onCreated, totalCount }) => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("Music Video");
  const [busy, setBusy] = useState(false);
  const [pinTop, setPinTop] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !url) return;
    setBusy(true);
    try {
      const created = await api.createProject({ title, youtube_url: url, category, order: pinTop ? -1 : totalCount });
      // If pinTop, set order to -1 then re-fetch (order=-1 means before all)
      if (pinTop) {
        await api.updateProject(created.id, { order: -1 });
      }
      toast.success("Latest project added — appears at top of the reel.");
      setTitle("");
      setUrl("");
      onCreated();
    } catch {
      toast.error("Failed to add. Check the YouTube URL.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="quick-add-form">
      <input
        className="input-cinema"
        placeholder="Project Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        data-testid="quick-add-title"
      />
      <input
        className="input-cinema md:col-span-2"
        placeholder="https://youtu.be/..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        data-testid="quick-add-url"
      />
      <select
        className="input-cinema bg-onyx-800"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      >
        <option>Music Video</option>
        <option>Commercial</option>
        <option>Documentary</option>
        <option>Short Film</option>
        <option>Feature Film</option>
      </select>
      <label className="flex items-center gap-2 text-xs text-ivory/65 col-span-1 md:col-span-3">
        <input
          type="checkbox"
          checked={pinTop}
          onChange={(e) => setPinTop(e.target.checked)}
          className="accent-gold"
          data-testid="quick-add-pin"
        />
        Pin as latest (appears first in homepage reel)
      </label>
      <button
        type="submit"
        disabled={busy}
        className="bg-gold hover:bg-gold-bright disabled:opacity-50 text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase py-3"
        data-testid="quick-add-submit"
      >
        {busy ? "Adding…" : "Add to Reel"}
      </button>
    </form>
  );
};

// ============== Blogs Manager ==============
const BlogsManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const blank = { title: "", excerpt: "", body: "", cover_url: "", tags: "", published: true };
  const [form, setForm] = useState(blank);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listBlogsAdmin();
      setBlogs(list || []);
    } catch (err) {
      console.error("Failed to load blogs:", err);
      toast.error("Failed to load blogs.");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required.");
      return;
    }
    const payload = {
      ...form,
      tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (editing) {
        await api.updateBlog(editing, payload);
        toast.success("Story updated.");
      } else {
        await api.createBlog(payload);
        toast.success("Story published.");
      }
      setForm(blank);
      setEditing(null);
      setCreating(false);
      refresh();
    } catch {
      toast.error("Save failed.");
    }
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({
      title: b.title,
      excerpt: b.excerpt || "",
      body: b.body || "",
      cover_url: b.cover_url || "",
      tags: (b.tags || []).join(", "),
      published: b.published,
    });
    setCreating(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    await api.deleteBlog(id);
    refresh();
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-12" data-testid="blogs-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-display text-3xl text-ivory">Journal</h2>
          <p className="text-ivory/55 text-sm mt-1">{blogs.length} stories</p>
        </div>
        <button
          onClick={() => {
            setCreating(!creating);
            setEditing(null);
            setForm(blank);
          }}
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold-bright text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-5 py-3"
          data-testid="new-blog-btn"
        >
          <Plus size={14} /> {creating ? "Close" : "New Story"}
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="gold-border bg-onyx-800 p-6 mb-10 grid grid-cols-1 gap-5" data-testid="blog-form">
          <input
            className="input-cinema"
            placeholder="Story title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="blog-title-input"
          />
          <input
            className="input-cinema"
            placeholder="Cover image URL (Unsplash / uploaded /api/files/... / YouTube thumbnail)"
            value={form.cover_url}
            onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
          />
          <input
            className="input-cinema"
            placeholder="Tags (comma separated): Punjabi, BTS, Production"
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
          />
          <textarea
            rows={2}
            className="input-cinema resize-none"
            placeholder="Short excerpt (1-2 lines, shown on cards)"
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          />
          <textarea
            rows={10}
            className="input-cinema resize-none"
            placeholder="Full story body…"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            data-testid="blog-body-input"
          />
          <label className="flex items-center gap-2 text-sm text-ivory/70">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="accent-gold"
            />
            Published (visible on public site)
          </label>
          <div className="flex gap-3">
            <button
              type="submit"
              className="bg-gold hover:bg-gold-bright text-onyx-900 font-cinzel text-[10px] tracking-[0.3em] uppercase px-6 py-3"
              data-testid="blog-save-btn"
            >
              {editing ? "Update Story" : "Publish Story"}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setEditing(null);
                setForm(blank);
              }}
              className="border border-gold/30 text-ivory font-cinzel text-[10px] tracking-[0.3em] uppercase px-6 py-3"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {blogs.length === 0 && !creating && (
          <div className="gold-border bg-onyx-800 p-12 text-center text-ivory/50">
            <FileText size={32} className="text-gold/50 mx-auto mb-4" />
            No stories yet. Click "New Story" to publish your first journal entry.
          </div>
        )}
        {blogs.map((b) => (
          <div key={b.id} className="gold-border bg-onyx-800 p-5 flex flex-col md:flex-row gap-5 items-start" data-testid={`blog-row-${b.id}`}>
            {b.cover_url && (
              <img src={b.cover_url} alt={b.title} className="w-full md:w-48 aspect-video object-cover gold-border" onError={(e) => (e.currentTarget.style.opacity = "0.3")} />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-display text-2xl text-ivory">{b.title}</h3>
                {!b.published && <span className="text-[9px] font-cinzel tracking-[0.3em] uppercase text-ivory/50 border border-ivory/30 px-2 py-0.5">Draft</span>}
              </div>
              <p className="text-ivory/60 text-sm">{b.excerpt}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {(b.tags || []).map((t) => (
                  <span key={t} className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold border border-gold/30 px-2 py-0.5">{t}</span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 self-start">
              <button onClick={() => startEdit(b)} className="border border-gold/30 hover:border-gold text-ivory font-cinzel text-[10px] tracking-[0.3em] uppercase px-4 py-2" data-testid={`blog-edit-${b.id}`}>
                Edit
              </button>
              <button onClick={() => remove(b.id)} className="border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-2" data-testid={`blog-delete-${b.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
